import type React from "react"
import { ArrowRight, ExternalLink, HelpingHand } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import Footer from "./footer"
import NavBarLandingPage from "./navBar"
import { useTranslation } from "react-i18next"
import { tokens } from "@/locales/translations/tokens"

interface CommunityCardProps {
  title: string
  description: string
  buttonText: string
  buttonLink: string
  icon: React.ReactNode
}

const CommunityCard = ({ title, description, buttonText, buttonLink }: CommunityCardProps) => {
  const { t } = useTranslation();
  return (
    <Card
      className="max-w-md w-full p-6 text-center relative border border-muted-foreground/20 transition-all duration-200 
      hover:border-primary hover:ring-1 hover:ring-primary cursor-pointer"
      onClick={() => window.open(buttonLink, "_blank")}
    >
      <div className="flex justify-center my-4">
        {title === t(tokens.landing.community_title) ? (
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/slack/slack-original.svg" alt="Slack Logo" width={80} height={80} />
        ) : (
          <HelpingHand className="w-20 h-20 text-primary" />
        )}
      </div>

      <Separator className="my-2" />

      <h3 className="text-xl font-semibold my-4">{title}</h3>
      <p className="mb-8 text-muted-foreground">{description}</p>

      <Button className="w-full mb-2" variant="default">
        {buttonText}
        <ExternalLink className="ml-2 h-4 w-4" />
      </Button>
    </Card>
  )
}

export default function Community() {
  const { t } = useTranslation();
  const cards = [
    {
      title: t(tokens.landing.community_title),
      description: t(tokens.landing.slack_community_description),
      buttonText: t(tokens.landing.join_slack),
      buttonLink: "https://join.slack.com/share/enQtODc1MDY2NDYwNjk2Ny0zZmZhMmUzNjU3ZjYxODI4YjVjOTdmOTU0MjRjZDAwNGQ4MzViMzBmZGYzYzJlYTQ1NzY0MGUzMDdjZmFmYmIz",
    },
    {
      title: t(tokens.landing.support_title),
      description: t(tokens.landing.support_description),
      buttonText: t(tokens.landing.platform),
      buttonLink: "/sign-in",
    },
  ]

  return (
    // <div>
    <div className="relative w-screen flex flex-col items-center justify-center gap-6">
      <div className="absolute inset-0 -z-50 bg-[url('/images/blue_futuristic_min.jpg')] bg-cover bg-center opacity-5"></div>
      <NavBarLandingPage />
      <div className="w-full max-w-7xl mx-auto px-4 py-12">
        <div className="w-full max-w-7xl mt-10 mx-auto rounded-2xl py-4">
          <div className="container max-w-4xl">
            <h1 className="text-2xl md:text-4xl text-center font-semibold pt-8 text-foreground">
              Alan Community Network
            </h1>
            <p className="text-base md:text-xl text-center pb-8 text-muted-foreground">
              {t(tokens.landing.community_text)}
            </p>

            <div className="flex flex-col justify-center gap-8 py-8">
              {/* Cards Section */}
              <div className="flex flex-col sm:flex-row justify-center items-center gap-8">
                {cards.map((card, index) => (
                  <CommunityCard key={index} {...card} icon={null} />
                ))}
              </div>

              {/* Call to Action Section */}
              <div className="border-primary/1 py-16 rounded-md flex flex-col justify-center gap-8">
                <h2 className="text-2xl md:text-3xl text-center font-semibold text-foreground">{t(tokens.landing.get_started_cta)}</h2>
                <p className="text-base md:text-xl text-center pb-4 text-muted-foreground">{t(tokens.landing.see_documentation)}</p>

                <div className="flex flex-col sm:flex-row px-4 sm:px-0 justify-center gap-4">
                  <Button 
                    variant="outline" 
                    className="group"
                    onClick={() => window.open("https://docs.mrturing.ai/", "_blank")}
                    >
                    {t(tokens.landing.api_documentation)}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
